import { DEFAULT_BIG_DIALOG_WIDTH } from '@abb-procure/constants';
import { PeoplePickerApiService, PeoplePickerUser } from '@abb-procure/data';
import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  ViewEncapsulation,
  booleanAttribute,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { CKEditor5, CKEditorModule } from '@ckeditor/ckeditor5-angular';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { NgChanges } from '../../utilities/angular-types.util';
import { isRequired } from '../../utilities/form-control.util';
import { Button } from '../button/button.component';
import {
  RichTextDialog,
  RichTextDialogData,
} from '../rich-text-dialog/rich-text-dialog.component';
import { ShowField } from '../show-field/show-field.component';

type MentionUser = {
  id: string;
  userId: number;
  name: string | null;
  email: string | null;
};

@Component({
  selector: 'procure-input-rich-text',
  templateUrl: './input-rich-text.component.html',
  styleUrl: './input-rich-text.component.scss',
  imports: [
    MatIcon,
    MatTooltip,
    Button,
    CKEditorModule,
    ReactiveFormsModule,
    ShowField,
    SafeHtmlPipe,
    MatLabel,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class InputRichText implements OnChanges {
  @Input() control!: FormControl;
  @Input() label: string = '';
  @Input() hint: string = '';
  @Input() placeholder: string = '';
  @Input({ transform: booleanAttribute }) showMode: boolean = false;
  @Input({ transform: booleanAttribute }) showFullScreenButton: boolean = true;
  @Input({ transform: booleanAttribute }) invalid: boolean | undefined = false;
  @Input({ transform: booleanAttribute }) enableMentions: boolean = false;
  @Input({ transform: booleanAttribute }) html: boolean = false;

  @Input() toolbar: string[] = [
    'bold',
    'italic',
    'underline',
    '|',
    'fontColor',
    'fontBackgroundColor',
    '|',
    'alignment',
    '|',
    'bulletedList',
    'numberedList',
    '|',
    'link',
  ];

  readonly editor = ClassicEditor;

  get editorConfig(): CKEditor5.Config {
    return {
      placeholder: this.placeholder,
      toolbar: this.toolbar,
      mention: this.enableMentions
        ? {
            feeds: [
              {
                marker: '@',
                feed: this.getFeedItems.bind(this),
                itemRenderer: this.renderMention.bind(this),
              },
            ],
          }
        : null,
      htmlSupport: this.html
        ? {
            allow: [
              {
                name: /.*/,
                attributes: true,
                classes: true,
                styles: true,
              },
            ],
          }
        : null,
      removePlugins: ['FontSize'],
    };
  }

  get isRequired(): boolean {
    return isRequired(this.control);
  }

  get value(): string {
    if (this.control?.value) {
      return this.control.value;
    }

    return '-';
  }

  private readonly changeRef = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly peoplePickerApiService = inject(PeoplePickerApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);

  ngOnChanges(changes: NgChanges<InputRichText>): void {
    if (
      changes.showMode &&
      changes.showMode.currentValue !== changes.showMode.previousValue
    ) {
      this.changeRef.markForCheck();
    }
  }

  async enterFullscreen(): Promise<void> {
    const module =
      await import('../rich-text-dialog/rich-text-dialog.component');

    const dialog = this.dialog.open<RichTextDialog, RichTextDialogData, string>(
      module.RichTextDialog,
      {
        width: DEFAULT_BIG_DIALOG_WIDTH,
        data: {
          value: this.control.value,
          label: this.label,
        },
        panelClass: ['rich-text-dialog'],
        scrollStrategy: this.overlay.scrollStrategies.block(),
      },
    );

    dialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response) {
          this.control.setValue(response);
        }
      });
  }

  private async getFeedItems(filterString: string): Promise<MentionUser[]> {
    if (!filterString) {
      return [];
    }

    const response = await this.peoplePickerApiService.getUsers({
      searchText: filterString,
      existingInDb: undefined,
    });

    return (response ?? []).slice(0, 10).map((user) => ({
      id: `@${user.name}`,
      name: user.name,
      userId: user.id,
      email: user.email,
    }));
  }

  private renderMention(item: PeoplePickerUser): HTMLDivElement {
    const itemElement = document.createElement('div');
    itemElement.textContent = `${item.name} (${item.email})`;
    return itemElement;
  }
}
